/**
 * PayablesScreen — supplier outstanding: who the shop owes, aged 0–30 / 31–60 /
 * 61–90 / 90+, with overdue (past credit_days) and over-credit-limit flags. The
 * mirror of the Receivables khata. A position as of now, not a range.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { toDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadPayables } from '@/reports/payables/payablesDataSource';
import {
  buildPayablesAging,
  BUCKET_LABELS,
  type PayablesAging,
} from '@/reports/payables/payablesAging';

// ─── Component ───────────────────────────────────────────────────────────────

export function PayablesScreen() {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const [data, setData]           = useState<PayablesAging | null>(null);
  const [asOf, setAsOf]           = useState<Date>(new Date());
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      const now = new Date();
      setAsOf(now);
      const source = await loadPayables(merchantId, now);
      setData(buildPayablesAging(source));
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { void load(); }, [load]);

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.rows, [
        { header: 'Supplier', value: (r) => r.name },
        { header: 'Phone', value: (r) => r.phone ?? '' },
        { header: '0-30', value: (r) => r.buckets[0] },
        { header: '31-60', value: (r) => r.buckets[1] },
        { header: '61-90', value: (r) => r.buckets[2] },
        { header: '90+', value: (r) => r.buckets[3] },
        { header: 'Total', value: (r) => r.total },
        { header: 'Overdue', value: (r) => r.overdueAmount },
        { header: 'Unaged', value: (r) => r.unaged },
        { header: 'Credit days', value: (r) => r.creditDays },
        { header: 'Credit limit', value: (r) => r.creditLimit },
        { header: 'Over limit', value: (r) => (r.overLimit ? 'YES' : '') },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `Payables_${toDateInputValue(asOf)}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildPayablesHtml(data, asOf),
        defaultFileName: `Payables_${toDateInputValue(asOf)}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-fg-tertiary">
          As of {asOf.toLocaleDateString('en-IN')}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.rows.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">
          No outstanding payables. Every supplier bill is settled.
        </div>
      ) : (
        <>
          {/* Unaged disclosure */}
          {data.unagedTotal > 0.01 && (
            <div className="flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
              <Info size={16} className="mt-0.5 shrink-0 text-accent-ink" />
              <div className="text-body text-fg-secondary">
                {formatCurrency(data.unagedTotal)} of the balance couldn’t be dated from the purchase
                ledger (opening balances or adjustments) and is shown in the <span className="font-medium">90+</span> bucket.
                Totals still match each supplier’s recorded balance.
              </div>
            </div>
          )}

          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Total payable" value={formatCurrency(data.totals.total)} accent />
            <KPI label="Overdue" value={formatCurrency(data.totals.overdueAmount)}
              warn={data.totals.overdueAmount > 0} />
            <KPI label="Suppliers owed" value={String(data.supplierCount)} />
            <KPI label="Over credit limit" value={String(data.overLimitCount)}
              warn={data.overLimitCount > 0} />
          </div>

          {/* Bucket summary */}
          <Card title="Aging summary">
            <div className="grid grid-cols-4 gap-3">
              {BUCKET_LABELS.map((label, i) => (
                <div key={label} className="text-center">
                  <div className="text-label uppercase text-fg-tertiary">{label} days</div>
                  <div className={cn('mt-1 text-body font-medium tabular-nums',
                    i >= 2 ? 'text-warning-ink' : 'text-fg-primary')}>
                    {formatCurrency(data.totals.buckets[i])}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Per-supplier table */}
          <Card title="Outstanding by supplier">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Supplier</th>
                    {BUCKET_LABELS.map((l) => (
                      <th key={l} className="pb-1 text-right font-normal">{l}</th>
                    ))}
                    <th className="pb-1 text-right font-normal">Total</th>
                    <th className="pb-1 text-right font-normal">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.supplierId} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-fg-primary">{r.name}</span>
                          {r.overLimit && (
                            <span title="Over credit limit"
                              className="flex items-center gap-0.5 rounded bg-warning/10 px-1 text-label text-warning-ink">
                              <AlertTriangle size={10} /> limit
                            </span>
                          )}
                        </div>
                        {r.phone && <div className="text-label text-fg-tertiary">{r.phone}</div>}
                      </td>
                      {r.buckets.map((b, bi) => (
                        <td key={bi} className={cn('py-1.5 text-right tabular-nums',
                          b === 0 ? 'text-fg-tertiary/40' : bi >= 2 ? 'text-warning-ink' : 'text-fg-secondary')}>
                          {b === 0 ? '–' : formatCurrency(b)}
                        </td>
                      ))}
                      <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">
                        {formatCurrency(r.total)}
                      </td>
                      <td className={cn('py-1.5 text-right tabular-nums',
                        r.overdueAmount > 0 ? 'text-warning-ink' : 'text-fg-tertiary/40')}>
                        {r.overdueAmount > 0 ? formatCurrency(r.overdueAmount) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary">Total</td>
                    {data.totals.buckets.map((b, bi) => (
                      <td key={bi} className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(b)}</td>
                    ))}
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totals.total)}</td>
                    <td className="pt-2 text-right tabular-nums text-warning-ink">{formatCurrency(data.totals.overdueAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent, warn }: { label: string; value: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums',
        warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildPayablesHtml(d: PayablesAging, asOf: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const cell = (n: number) => (n === 0 ? '–' : fmt(n));

  const rows = d.rows.map((r) =>
    `<tr>
      <td>${escapeHtml(r.name)}${r.overLimit ? ' <span style="color:#b45309">(over limit)</span>' : ''}${r.phone ? `<br><span style="color:#999;font-size:11px">${escapeHtml(r.phone)}</span>` : ''}</td>
      <td style="text-align:right">${cell(r.buckets[0])}</td>
      <td style="text-align:right">${cell(r.buckets[1])}</td>
      <td style="text-align:right">${cell(r.buckets[2])}</td>
      <td style="text-align:right">${cell(r.buckets[3])}</td>
      <td style="text-align:right;font-weight:600">${fmt(r.total)}</td>
      <td style="text-align:right">${cell(r.overdueAmount)}</td>
    </tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:12px}th,td{padding:6px 8px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child{text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Payables Aging</h1>
<p style="color:#888">As of ${asOf.toLocaleDateString('en-IN')} · ${d.supplierCount} supplier${d.supplierCount !== 1 ? 's' : ''} · Overdue ${fmt(d.totals.overdueAmount)}</p>
<table>
<thead><tr><th>Supplier</th><th>0–30</th><th>31–60</th><th>61–90</th><th>90+</th><th>Total</th><th>Overdue</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td>Total</td>
<td style="text-align:right">${fmt(d.totals.buckets[0])}</td>
<td style="text-align:right">${fmt(d.totals.buckets[1])}</td>
<td style="text-align:right">${fmt(d.totals.buckets[2])}</td>
<td style="text-align:right">${fmt(d.totals.buckets[3])}</td>
<td style="text-align:right">${fmt(d.totals.total)}</td>
<td style="text-align:right">${fmt(d.totals.overdueAmount)}</td></tr></tfoot>
</table>
${d.unagedTotal > 0.01 ? `<p style="color:#2563eb;font-size:11px">Note: ${fmt(d.unagedTotal)} could not be dated from the ledger (opening balances/adjustments) and sits in 90+.</p>` : ''}
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}
